"use client";
import { Open_Sans } from "next/font/google";
import { ThemeProvider } from "@/providers/theme-provider";
import Header from "@/components/common/Header";
import Sidebar from "@/components/common/Sidebar";
import PageTitle from "@/components/common/PageTitle";

import { useUser } from "@/zustand-hooks/user-hook";
import { useEffect } from "react";
import { Loader2Icon, Lock } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundaryComponent";
import useIsMounted from "@/hooks/useIsMounted";
import ErrorHandler from "@/utils/errorHandler";
import { APP_NAME, email } from "@/utils/config";
import CustomerMessage from "@/components/common/CustomerMessage";
import { BlockedAccountProvider } from "@/providers/BlockedAccountProvider";
import { useTranslations } from "next-intl";

const open_sans = Open_Sans({ subsets: ["latin"] });

export default function ProtectedLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const isMounted = useIsMounted();
  const { user, clientSettings, checkAuth } = useUser();
  const t = useTranslations("pages.Login.blocked");
  const isBlocked = !!user?.account_frozen;

  useEffect(() => {
    checkAuth();
    ErrorHandler.getInstance().init();
  }, []);

  if (user === undefined)
    return (
      <div className="absolute inset-0 bg-secondary font-bold text-6xl flex items-center justify-center text-white">
        <Loader2Icon
          className="animate-spin text-brand-500"
          width={50}
          height={50}
        />
      </div>
    );

  if (!isMounted) return null;

  return (
    <div className="flex max-w-full overflow-hidden max-h-screen bg-content">
      <ErrorBoundary>
        <Sidebar />
      </ErrorBoundary>

      <div className="flex flex-col flex-1 h-screen overflow-hidden">
        {isBlocked && (
          <div className="bg-red-600 text-white px-4 py-2 text-center font-semibold text-sm flex items-center justify-center gap-2 shrink-0">
            <Lock className="h-4 w-4" />
            {t("banner")}
          </div>
        )}

        <ErrorBoundary>
          <Header />
        </ErrorBoundary>

        <PageTitle />
        <div className="flex flex-1 flex-col p-6 h-full overflow-y-auto relative bg-content">
          <ErrorBoundary>
            <CustomerMessage />
            <BlockedAccountProvider>
              {children}
            </BlockedAccountProvider>
          </ErrorBoundary>
        </div>
        <footer className="bg-gradient-to-r from-chrome-800 to-chrome-900 dark:from-chrome-900 dark:to-chrome-950 border-t border-chrome-700/50">
          <div className="px-6 py-4">
           
            {
              clientSettings?.footer_message ? (
                <p className="text-xs text-chrome-400 mt-1 whitespace-pre-wrap">
                  {clientSettings.footer_message}
                </p>
              )
              : 
              <h1 className="font-semibold text-chrome-400 text-sm">
              © {APP_NAME} {new Date().getFullYear()}
            </h1>
            }
          </div>
        </footer>
      </div>
    </div>
  );
}
